import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import Footer from '../components/Footer';
import axios from 'axios';
import { toast } from 'sonner';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { ArrowLeft, Clock, DollarSign } from 'lucide-react';
import { API_URL } from '../lib/apiBase';

const RoadmapPage = () => {
  const navigate = useNavigate();
  const { currentWorkspaceId } = useWorkspace();
  const [roadmap, setRoadmap] = useState({
    planned: [],
    in_progress: [],
    shipped: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentWorkspaceId) fetchRoadmap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspaceId]);

  const fetchRoadmap = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/roadmap`, {
        params: { workspace_id: currentWorkspaceId },
      });
      setRoadmap(response.data);
    } catch (error) {
      console.error('Error fetching roadmap:', error);
      toast.error('Failed to load roadmap');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      key: 'planned',
      title: '📅 Planned',
      color: 'border-blue-500',
      bgColor: 'bg-blue-50 dark:bg-blue-950/20'
    },
    {
      key: 'in_progress',
      title: '🛠️ In Progress',
      color: 'border-amber-500',
      bgColor: 'bg-amber-50 dark:bg-amber-950/20'
    },
    {
      key: 'shipped',
      title: '✅ Shipped',
      color: 'border-green-500',
      bgColor: 'bg-green-50 dark:bg-green-950/20'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="container-wide mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/app')}
                data-testid="roadmap-back-button"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
              <div>
                <h1 className="text-2xl font-bold">Product Roadmap</h1>
                <p className="text-sm text-muted-foreground">Track what's planned, in progress, and shipped</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Roadmap Kanban Board */}
      <div className="container-wide mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {columns.map((column) => {
            const items = roadmap[column.key] || [];
            
            return (
              <div key={column.key} className="space-y-4">
                {/* Column Header */}
                <Card className={`border-t-4 ${column.color}`}>
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <h2 className="font-semibold">{column.title}</h2>
                      <Badge variant="secondary">{items.length}</Badge>
                    </div>
                  </div>
                </Card>

                {/* Column Items */}
                <ScrollArea className="h-[calc(100vh-250px)]">
                  <div className="space-y-3">
                    {items.length === 0 ? (
                      <Card className="p-6 text-center text-sm text-muted-foreground">
                        No items yet
                      </Card>
                    ) : (
                      items.map((item, index) => (
                        <motion.div
                          key={item.signal_id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <Card className={`p-4 space-y-3 hover:shadow-md transition-shadow ${column.bgColor}`}>
                            <div>
                              <h3 className="text-sm font-semibold mb-1">{item.summary}</h3>
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge variant="secondary" className="text-xs">
                                  {item.category}
                                </Badge>
                                {item.signal && (
                                  <Badge variant="outline" className="text-xs">
                                    {item.signal.user_segment}
                                  </Badge>
                                )}
                              </div>
                            </div>

                            {/* Revenue Estimate */}
                            {item.revenue_estimate && item.revenue_estimate.potential_mrr > 0 && (
                              <div className="flex items-center gap-2 text-xs">
                                <DollarSign className="h-3.5 w-3.5 text-green-500" />
                                <span className="font-semibold">
                                  ${(item.revenue_estimate.potential_mrr / 1000).toFixed(1)}k/mo potential
                                </span>
                              </div>
                            )}

                            {/* Status Updated Time */}
                            {item.status_updated_at && (
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Clock className="h-3 w-3" />
                                <span>
                                  {new Date(item.status_updated_at).toLocaleDateString()}
                                </span>
                              </div>
                            )}
                          </Card>
                        </motion.div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <Footer variant="minimal" />
    </div>
  );
};

export default RoadmapPage;
